import styled, { css } from 'styled-components/macro';
import { bool, number } from 'prop-types';

import NoData from 'components/styled/NoData/NoData';

import ContentLoader from 'components/utility/ContentLoader/ContentLoader';
import TableCell from 'components/data-visualization/Table/Cell';
import TableRow from 'components/data-visualization/Table/Row';
import useMedia from 'hooks/useMedia';

const NoDataOverlay = styled.div`
  ${({ isSmallScreen }) =>
    !isSmallScreen &&
    css`
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    `};

  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Renders two grey boxes that mimic table cell content (only two cells so it will render on smaller screens correctly)
 * The length of the boxes is determined by the curren screen size.
 * @param {number} columnCount the number of columns of the table in which the placeholder will be rendered
 * @param {string} className optional class name for the rendered ContentLoader
 * @param {object} cellComponent component name for the cell, defaults to TableCell
 * @param {object} rowComponent component name for the row, defaults to TableRow
 */
const TableRowsPlaceholder = (props) => {
  const {
    columnCount,
    className,
    cellComponent: CellComponent = TableCell,
    rowComponent: RowComponent = TableRow,
  } = props;

  const loaderCount = 2;
  const cols = Array(columnCount).fill();

  const isLargeScreen = useMedia('(min-width: 1450px)');
  const placeholderWidth = isLargeScreen ? 200 : 100;
  const smallPlaceHolderWidth = (placeholderWidth / 100) * 62.5;

  return Array(loaderCount)
    .fill()
    .map((val, index) => (
      <RowComponent enableHover={false} key={`playlist-table-loader-${index}`}>
        {cols.map((c, index) => {
          if (index < 2) {
            return (
              <CellComponent key={`btph_col_${index}`}>
                <ContentLoader
                  width={placeholderWidth}
                  height={37}
                  className={className}
                  speed={0}
                >
                  <rect width={smallPlaceHolderWidth} height="15" x="0" y="4" />
                  <rect width={placeholderWidth} height="10" x="0" y="25" />
                </ContentLoader>
              </CellComponent>
            );
          } else {
            return (
              <CellComponent key={`btph_col_${index}`}>&nbsp;</CellComponent>
            );
          }
        })}
      </RowComponent>
    ));
};

/**
 * Placeholder overlay for the breakdown Table. This overlays the table when there are no items in the table.
 * @param {number} columnCount the number of columns of the table in which the placeholder will be rendered
 * @param {bool} isSmallScreen indicates whether the current screen is small, usually meaning its being rendered on a mobile device
 */
const BreakdownTablePlaceholder = (props) => {
  const { columnCount, isSmallScreen } = props;
  return (
    <>
      {!isSmallScreen && <TableRowsPlaceholder columnCount={columnCount} />}
      <tr>
        <td colSpan={columnCount}>
          <NoDataOverlay isSmallScreen={isSmallScreen}>
            <NoData isSmallScreen={isSmallScreen}>
              <h3>No data available</h3>
            </NoData>
          </NoDataOverlay>
        </td>
      </tr>
    </>
  );
};

BreakdownTablePlaceholder.defaultProps = {
  isSmallScreen: false,
};

BreakdownTablePlaceholder.propTypes = {
  /** The number of columns to show behind the no data overlay */
  columnCount: number.isRequired,
  /** Is this a small screen? Used to the table and make the message static */
  isSmallScreen: bool,
};

/** @component */
export default BreakdownTablePlaceholder;
